/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from 'react';
import { Container, Button, Row, Col, Card, Nav, Tab } from 'react-bootstrap';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { adminApi } from '../../api';

import PageTitle from '../../components/common/PageTitle';
import Loader from '../../components/common/Loader';
import FreezeModal from '../../components/common/FreezeModal';
import EndUserProfile from '../../components/admin/EndUserProfile';
import VerificationRecords from '../../components/admin/VerificationRecords';
import EndUserDocuments from '../../components/admin/EndUserDocuments';
import PaymentMethods from '../../components/common/endusers/PaymentMethods';
import ConfirmModal from '../../components/common/ConfirmModal';
import TooltipButton from '../../components/common/TooltipButton';
import { formatFreezeReasons, hasSuperuserAccess } from '../../utils';
import { SUPERUSER_LEVEL } from '../../constants';

function EndUser({ t, user, setAlert }) {
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const location = useLocation();
  const { action, handle, pi_id } = useParams();

  const data = location.state?.data;
  const { totalEndUsers, currentIndex, nextHandle, prevHandle } = useMemo(() => {
    let total = 0;
    let currentIdx = null;
    let nextH = null;
    let prevH = null;

    if (data) {
      total = data.length;
      for (let i = 0; i < total; i += 1) {
        if (handle === data[i].user_handle) {
          currentIdx = i;
          if (i !== 0) prevH = data[i - 1].user_handle;
          if (i !== total - 1) nextH = data[i + 1].user_handle;
          break;
        }
      }
    }

    return {
      totalEndUsers: total,
      currentIndex: currentIdx,
      nextHandle: nextH,
      prevHandle: prevH,
    };
  }, [data, handle]);

  const [key, setKey] = useState('profile');
  const [redirect, setRedirect] = useState(false);
  const [verificationStatuses, setVerificationStatuses] = useState([]);
  const [endUser, setEndUser] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    handle: '',
    emails: [{ email: '', action: 'add' }],
    phones: [{ phone: '', action: 'add' }],
    birthdate: '',
    addresses: [
      {
        action: 'add',
        street_address_1: '',
        street_address_2: null,
        city: '',
        state: '',
        postal_code: '',
      },
    ],
    identities: [
      {
        action: 'add',
        identity_type: 'SSN',
        identity: '',
      },
    ],
    apps: [],
    verification_status: false,
    certification_status: '',
    verification_history: [],
    member_verifications: [],
    onboarded_at_priority: false,
    migrated_to_priority: false,
    sila_id: '',
  });

  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [achTransactions, setAchTransactions] = useState(false);
  const [txnDetails, setTxnDetails] = useState(undefined);
  const [isLoadingEndUser, setIsLoadingEndUser] = useState(false);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);

  const fetchEndUserData = async (callback) => {
    try {
      const response = await adminApi.getEndUser(handle);
      if (response.success) {
        let taxIDs = response.identities.filter(
          (identity) =>
            identity.identity &&
            identity.identity_type &&
            ['ssn', 'ein'].includes(identity.identity_type.toLowerCase())
        );
        if (!taxIDs.length) {
          taxIDs = endUser.identities;
          if (response.entity_type === 'business') {
            taxIDs[0].identity_type = 'EIN';
          }
        }

        setEndUser({
          first_name: response.entity.first_name,
          last_name: response.entity.last_name,
          full_name:
            response.entity.entity_name ||
            `${response.entity.first_name} ${response.entity.last_name}`,
          handle: response.user_handle,
          emails: response.emails.length ? [response.emails[0]] : endUser.emails,
          phones: response.phones.length ? [response.phones[0]] : endUser.phones,
          birthdate: response.entity.birthdate,
          addresses: response.addresses.length ? [response.addresses[0]] : endUser.addresses,
          identities: taxIDs,
          entity_type: response.entity_type,
          apps: response.apps,
          memberships:
            response.entity_type === 'individual' ? response.memberships : response.members,
          member_verifications:
            response.entity_type === 'business' ? response.member_verifications : null,
          verification_history: response.verification_history,
          verification_status: response.verification_status,
          certification_status: response.certification_status,
          frozen: response.entity.frozen,
          freeze_reasons: response.entity.freeze_reasons ? response.entity.freeze_reasons : {},
          onboarded_at_priority: response.entity.onboarded_at_priority || false,
          migrated_to_priority: response.entity.migrated_to_priority || false,
          sila_id: response.entity.sila_id || 'N/A',
          allIdentities: response.identities.filter((identity) => identity.identity),
        });
        if (callback) callback(response);
        setIsLoadingEndUser(false);
      } else {
        setRedirect(true);
      }
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      setRedirect(true);
    }
  };

  const fetchVerificationStatuses = async (callback) => {
    try {
      const response = await adminApi.getVerificationStatuses();
      if (response.success) {
        setVerificationStatuses(response.verification_statuses);
        if (callback) callback(response);
        setIsLoadingStatuses(false);
      } else {
        setRedirect(true);
      }
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      setRedirect(true);
    }
  };

  useEffect(() => {
    setKey(action);
  }, [action]);

  useEffect(() => {
    if (redirect) navigate({ pathname: '/admin/end_users', state: { from: location.pathname } });
  }, [redirect]);

  useEffect(() => {
    setIsLoadingEndUser(true);
    fetchEndUserData();
  }, [handle]);

  useEffect(() => {
    if (endUser.verification_history.length === 0 && key !== 'profile') {
      navigate(`/admin/end_user/${handle}/profile`, { state: { data } });
    }
  }, [endUser]);

  useEffect(() => {
    setIsLoadingStatuses(true);
    fetchVerificationStatuses();
  }, []);

  const userFreezeUnfreeze = async ({ freeze_reasons }) => {
    const reasonsWithStatus = formatFreezeReasons(
      Object.keys(endUser.freeze_reasons),
      new Set(freeze_reasons)
    );

    const response = await adminApi.userFreezeUnfreeze(reasonsWithStatus, endUser.handle);
    fetchEndUserData();
    if (response.success) {
      setAlert({
        message: response.message,
        variant: 'success',
      });
    } else if (response.status_code === 400) {
      setAlert({
        message: response.message,
        variant: 'danger',
      });
    } else if (response.status_code === 409) {
      setAlert({
        message: response.message,
        variant: 'warning',
      });
    }
    if (response.frozen && response.unsubmitted_transactions) {
      setAchTransactions(true);
      setTxnDetails({ transactions: response.unsubmitted_transactions });
    }
    setShowFreezeModal(false);
  };

  const cancelACHTransactions = async (values) => {
    if (values) {
      try {
        const response = await Promise.all(
          values.transactions.map((transaction) =>
            adminApi.cancelACHTransactions({ user_handle: values.user_handle, transaction })
          )
        );
        let message;
        if (response.some((res) => !res.success)) {
          response.forEach((res) => {
            // eslint-disable-next-line no-undef
            if (!res.success) throw handleResponseError(res);
            else message = response.message;
          });
        }
        setAchTransactions(false);
        setTxnDetails(undefined);
        setAlert({ message: message, variant: 'success' });
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      }
    } else {
      setAchTransactions(false);
      setTxnDetails(undefined);
    }
  };

  // variable used in FreezeModal
  const name = endUser.full_name || `${endUser.first_name} ${endUser.last_name}`;
  const type = t('common:endUsers.labels.singular');

  return (
    <Container className="main-content-container p-3 p-md-5 d-flex flex-column flex-grow-1">
      <Row className="g-0 page-header mb-3 mb-md-5">
        <PageTitle title={t('admin.endUsers.profile.title')} backBtn="/admin/end_users" />
        {data && (
          <div className="d-flex justify-content-between mt-3">
            <Link to={`/admin/end_user/${prevHandle}/${key}`} state={{ data: data }}>
              <Button
                variant="link"
                className={`back text-info p-0 ${currentIndex === 0 ? 'd-none' : ''}`}
              >
                {t('common:buttons.previous')}
              </Button>
            </Link>
            <div>
              {`End User ${currentIndex + 1} of ${totalEndUsers}`}
              {totalEndUsers === 20 && (
                <TooltipButton
                  placement="top"
                  variant="link"
                  className="p-1"
                  popoverProps={{ style: { textAlign: 'center' } }}
                  text={t('admin.endUsers.labels.warning20plus')}
                  style={{ textDecoration: 'none' }}
                >
                  <i className="sila-icon info" />
                </TooltipButton>
              )}
            </div>
            <Link to={`/admin/end_user/${nextHandle}/${key}`} state={{ data: data }}>
              <Button
                variant="link"
                className={`next text-info p-0 ${
                  currentIndex === totalEndUsers - 1 ? 'd-none' : ''
                }`}
              >
                {t('common:buttons.next')}
              </Button>
            </Link>
          </div>
        )}
      </Row>
      {isLoadingEndUser || isLoadingStatuses ? (
        <Loader />
      ) : (
        <Card className="loaded flex-grow-1">
          <Card.Header className="d-flex p-4">
            <h3 className="m-0 me-auto">
              <i className={`sila-icon me-2 text-primary ${endUser.entity_type}`} />
              {endUser.full_name}
            </h3>
            <p
              className={`text-lg ${
                !endUser.frozen ? 'text-success' : 'text-danger'
              } ms-0 me-4 mb-0 d-flex text-start`}
            >
              {!endUser.frozen ? t('common:status.enabled') : t('common:status.disabled')}
            </p>
            <Button
              onClick={() => setShowFreezeModal(true)}
              className="generate me-3 rounded-1 py-1"
              variant="primary"
              size="sm"
            >
              {t(`common:freezeUnfreeze.modal.modify.button`)}
            </Button>
            {endUser.verification_status && (
              <p
                className={`text-lg mb-0 text-${
                  endUser.verification_status.includes('passed')
                    ? 'success'
                    : endUser.verification_status.includes('failed')
                    ? 'warning'
                    : 'primary'
                }`}
              >
                {endUser.entity_type === 'business' ? 'KYB' : 'KYC'}
                {
                  verificationStatuses.find((status) => status.name === endUser.verification_status)
                    .label
                }
              </p>
            )}
          </Card.Header>
          <Card.Body as={Row} className="p-0 g-0">
            <Tab.Container activeKey={key} defaultActiveKey={key} onSelect={(k) => setKey(k)}>
              <Col md="4" lg="3" xl="2" className="border-end">
                <Nav className="d-flex flex-column align-content-start h-100 py-4">
                  <Nav.Item className="w-100">
                    <Nav.Link
                      className={`d-block px-4 py-3${
                        key === 'profile' ? ' bg-primary-accent text-primary' : ' text-info'
                      }`}
                      eventKey="profile"
                      as={NavLink}
                      to={`/admin/end_user/${handle}/profile`}
                      state={{ from: location.pathname, data: data }}
                    >
                      {t('admin.endUsers.navigation.profile')}
                    </Nav.Link>
                  </Nav.Item>
                  {endUser.verification_history.length !== 0 && (
                    <>
                      <Nav.Item className="w-100">
                        <Nav.Link
                          className={`d-block px-4 py-3${
                            key === 'records' ? ' bg-primary-accent text-primary' : ' text-info'
                          }`}
                          eventKey="records"
                          as={NavLink}
                          to={`/admin/end_user/${handle}/records`}
                          state={{ from: location.pathname, data: data }}
                        >
                          {t('admin.endUsers.navigation.records')}
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item className="w-100">
                        <Nav.Link
                          className={`d-block px-4 py-3${
                            key === 'docs' ? ' bg-primary-accent text-primary' : ' text-info'
                          }`}
                          eventKey="docs"
                          as={NavLink}
                          to={`/admin/end_user/${handle}/docs`}
                          state={{ from: location.pathname, data: data }}
                        >
                          {t('admin.endUsers.navigation.docs')}
                        </Nav.Link>
                      </Nav.Item>
                    </>
                  )}
                  <Nav.Item className="w-100">
                    <Nav.Link
                      className={`d-block px-4 py-3${
                        key === 'payment-instruments'
                          ? ' bg-primary-accent text-primary'
                          : ' text-info'
                      }`}
                      eventKey="payment-instruments"
                      as={NavLink}
                      to={`/admin/end_user/${handle}/payment-instruments`}
                      state={{ from: location.pathname, data: data }}
                    >
                      {t('admin.endUsers.navigation.paymentInstruments')}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Col>
              <Col md="8" lg="9" xl="10" className="p-4">
                <Tab.Content>
                  <Tab.Pane eventKey="profile">
                    <EndUserProfile
                      endUser={endUser}
                      setInitialValues={setEndUser}
                      getEndUser={fetchEndUserData}
                    />
                  </Tab.Pane>
                  {endUser.verification_history.length !== 0 && (
                    <>
                      <Tab.Pane eventKey="records">
                        <VerificationRecords
                          redirect={() => setRedirect(true)}
                          verificationStatuses={verificationStatuses}
                          endUser={endUser}
                          fetchEndUserData={fetchEndUserData}
                        />
                      </Tab.Pane>
                      <Tab.Pane eventKey="docs">
                        <EndUserDocuments initialValues={endUser} />
                      </Tab.Pane>
                    </>
                  )}
                  <Tab.Pane eventKey="payment-instruments">
                    <PaymentMethods
                      name={name}
                      handle={endUser.handle}
                      redirect={() => setRedirect(true)}
                      piUuid={pi_id}
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Col>
            </Tab.Container>
          </Card.Body>
        </Card>
      )}
      {showFreezeModal && (
        <FreezeModal
          show={showFreezeModal}
          data={{}}
          handleFreeze={userFreezeUnfreeze}
          reasons={endUser.freeze_reasons}
          setShow={setShowFreezeModal}
          isAdmin={hasSuperuserAccess(user, [SUPERUSER_LEVEL.FULL_ACCESS])}
          name={name}
          type={type}
        />
      )}

      {achTransactions && (
        <ConfirmModal
          size="lg"
          show={achTransactions}
          data={{ user_handle: endUser.handle, transactions: txnDetails.transactions }}
          title={t('admin.endUsers.freezeUnfreeze.model.achTransaction.title')}
          message={t('admin.endUsers.freezeUnfreeze.model.achTransaction.message', {
            handle: endUser.handle,
            transaction_count: txnDetails.transactions.length,
          })}
          buttonLabel={t('admin.endUsers.freezeUnfreeze.model.cancelTransaction.button')}
          cancelButtonLabel={t('admin.endUsers.freezeUnfreeze.model.approveTransaction.button')}
          onHide={cancelACHTransactions}
        />
      )}
    </Container>
  );
}

export default EndUser;
